// ============================================
// CHÁMOMILE — Browser, Cookie, Image Utilities
// ============================================

export const CookieManager = {
    set: (name, value, days = 7) => {
        const expires = new Date(Date.now() + days * 864e5).toUTCString();
        document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/; SameSite=Strict`;
    },
    get: (name) => {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        if (match) {
            try { return JSON.parse(decodeURIComponent(match[2])); } catch { return null; }
        }
        return null;
    },
    remove: (name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`;
    },
    saveCart: (cart) => CookieManager.set('chamomile_cart', cart, 7),
    getCart: () => CookieManager.get('chamomile_cart'),
    saveCustomerInfo: (info) => CookieManager.set('chamomile_customer', info, 7),
    getCustomerInfo: () => CookieManager.get('chamomile_customer'),
};

// FIX #5: Enhanced BrowserUtils with session caching for consistent detection
export const BrowserUtils = {
    _cachedInAppResult: null,

    isInstagramBrowser: () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        return ua.includes('Instagram');
    },
    isFacebookBrowser: () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        return ua.includes('FBAN') || ua.includes('FBAV') || ua.includes('FB_IAB');
    },
    isInAppBrowser: () => {
        // Check session cache first for consistency across the session
        const cacheKey = 'chamomile_inapp_browser';
        const cached = sessionStorage.getItem(cacheKey);
        if (cached !== null) {
            return cached === 'true';
        }

        // Detect and cache the result
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        const isInApp = ua.includes('Instagram') ||
            ua.includes('FBAN') ||
            ua.includes('FBAV') ||
            ua.includes('FB_IAB');

        // Cache in sessionStorage for consistency during this session
        try {
            sessionStorage.setItem(cacheKey, isInApp.toString());
        } catch (e) {
            // sessionStorage might be unavailable in some contexts
        }

        return isInApp;
    },
    getWhatsAppUrl: (phone, message) => {
        const encodedMsg = encodeURIComponent(message);
        return `https://wa.me/${phone}?text=${encodedMsg}`;
    },
    copyToClipboard: async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (err) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                document.body.removeChild(textarea);
                return true;
            } catch (e) {
                document.body.removeChild(textarea);
                return false;
            }
        }
    }
};

export const ImageUtils = {
    compressImage: (file, options = {}) => {
        const { maxWidth = 1200, maxHeight = 1200, quality = 0.8, type = 'image/jpeg' } = options;
        return new Promise((resolve, reject) => {
            if (file.size < 500 * 1024) { // Skip if < 500KB
                resolve(file);
                return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    let { width, height } = img;
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                    if (height > maxHeight) {
                        width = (width * maxHeight) / height;
                        height = maxHeight;
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, width, height);
                    ctx.drawImage(img, 0, 0, width, height);
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
                            resolve(compressedFile);
                        } else {
                            resolve(file);
                        }
                    }, type, quality);
                };
                img.onerror = () => resolve(file);
                img.src = e.target.result;
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    },
    generateThumbnail: (file, maxSize = 200) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let { width, height } = img;
                    if (width > height) {
                        if (width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
                    } else {
                        if (height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.6));
                };
                img.onerror = reject;
                img.src = e.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }
};

// FIX #2: Enhanced ImagePreloader with priority loading
export const ImagePreloader = {
    cache: new Map(),
    preloadProductImages: (products) => {
        products.forEach((product, index) => {
            if (!ImagePreloader.cache.has(product.imageUrl)) {
                const img = new Image();
                // First 3 images get high priority
                if (index < 3) {
                    img.fetchpriority = 'high';
                }
                img.src = product.imageUrl;
                img.onload = () => { ImagePreloader.cache.set(product.imageUrl, true); };
            }
        });
    },
    isLoaded: (url) => ImagePreloader.cache.has(url)
};

export const generateOrderId = () => {
    const ts = Date.now().toString(36).toUpperCase();
    const rnd = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `CHM-${ts}-${rnd}`;
};
